/* @flow */
import xhr from 'xhr';
import Config from 'Config';
import retry from 'retry';
import safeStringify from 'fast-safe-stringify';

import Cookies from 'lib/Cookies';
import uuid from 'utils/uuid';

import type { RouterState } from 'lib/ReactRouterTypes';

const ENABLED = Config.Environment.R101_EVENTS_ENABLED;
const INTERVAL = Config.Environment.R101_EVENTS_INTERVAL;
const URL = Config.Environment.R101_EVENTS_URL;

function decamelizeProperties(properties) {
  const props = {};
  for (const key in properties) {
    props[key.replace(/([a-z\d])([A-Z])/g, '$1_$2').toLowerCase()] = properties[key];
  }
  return props;
}

const windowDefined = (typeof window !== 'undefined');

export const ref = { xhr }; // tests :(

function dispatchBuffer(events: Object[], properties: Object, onError: (unsentEvents: Object[]) => void) {
  const data = safeStringify({
    batch: events,
    properties,
  });

  const operation = retry.operation({
    retries: 5,
    factor: 2, // exponential rollback
    minTimeout: 500,
    maxTimeout: 1000 * 60,
  });

  operation.attempt(function() {
    ref.xhr({
      url: URL + '/v1/import',
      method: 'POST',
      useXDR: true,
      data,
    }, function(err, res) {
      if (!res || res.statusCode >= 500) {
        const outOfRetries = !operation.retry(err);
        if (outOfRetries && err) {
          onError(events);
          console.warn('eventTracking failed to flush', operation.mainError(), res);
        }
      }
    });
  });

  // XXX (rgoldfinger) temporary while trying to find a circular reference
  const tempData = {
    batch: events,
    properties,
  };
  try {
    JSON.stringify(tempData);
  } catch (e) {
    console.error('Failed to serialize event data: ', safeStringify(tempData));
    throw new Error(e);
  }
}

type EventData = {
  event: string,
  timestamp: string,
  properties?: Object,
  'client_type'?: string,
  'client_uuid'?: string,
  'foreign_service'?: string,
  'request_url'?: string,
  'screen_width'?: number,
  'screen_height'?: number,
  'session_uuid'?: string,
};

export class EventTracking {
  doPush: boolean;
  buffer: EventData[];
  timeout: ?number;
  userId: ?number;
  userUuid: ?string;

  constructor(doPush: boolean) {
    this.doPush = doPush;
    this.buffer = [];
    this.timeout = null;
    this.userId = null;
    this.userUuid = null;
    if (doPush && windowDefined) {
      window.addEventListener('unload', this.flushBuffer.bind(this), false);
    }
  }

  setUserInfo(userId: ?number, userUuid: ?string) {
    this.userId = userId;
    this.userUuid = userUuid;
  }

  getProperties() {
    if (!windowDefined) {
      // TODO handle this more smartly in SSR
      return {};
    }

    return {
      client_type: 'Web',
      client_uuid: this.getClientId(),
      foreign_service: 'r101-web-client',
      request_url: window.location.href,
      screen_width: window.screen.width,
      screen_height: window.screen.height,
      session_uuid: this.getSessionId(),
    };
  }

  /**
   * SessionId uniquely identifies a session. A session is
   * begins when a user hits remind.com, and terminates when
   * the broswer closes or the user logs out.
   */

  getSessionId() {
    let sessionId = Cookies.get('session_uuid');
    if (!sessionId) {
      Cookies.set('session_uuid', sessionId = uuid());
    }
    return sessionId;
  }

  getClientId() {
    let clientId = Cookies.get('client_uuid');
    if (!clientId) {
      Cookies.set('client_uuid', clientId = uuid());
    }
    return clientId;
  }

  track(name: string, properties: Object = {}) {
    properties['user_id'] = this.userId;
    properties['user_uuid'] = this.userUuid;

    if (!this.doPush) {
      if (__DEV__) {
        console.debug('track', name, properties);
      }
      return;
    }

    this.buffer.push({
      event: name,
      properties,
      timestamp: new Date().toISOString(),
    });

    if (this.timeout === null) {
      this.timeout = setTimeout(() => {
        this.flushBuffer();
        this.timeout = null;
      }, INTERVAL);
    }
  }

  // FIXME contextName should be string but bad args are coming in
  tap(screenName: string, elementName: string,
    contextName: any = null, properties: Object = {}) {
    this.track('ui.tapped', Object.assign({
      screen_name: screenName,
      ui_element_name: elementName,
      ui_context: contextName,
    }, decamelizeProperties(properties)));
  }

  view(screenName: ?string, properties: Object = {}) {
    if (screenName) {
      this.track('ui.viewed', Object.assign({
        screen_name: screenName,
      }, decamelizeProperties(properties)));
    }
  }

  experimentExposure(experiment: string, variant: string, properties: Object = {}) {
    this.track('experiment.exposure', Object.assign({
      experiment,
      alternative: variant,
    }, decamelizeProperties(properties)));
  }

  flushBuffer(): void {
    if (this.buffer.length === 0) {
      return;
    }

    const onError = (unsentEvents: Object[]) => {
      this.buffer = this.buffer.concat(unsentEvents);
    };

    // shallow copy so that events added to the buffer during dispatch aren't sent twice
    const bufferCopy = [...this.buffer];
    this.buffer = [];
    try {
      dispatchBuffer(bufferCopy, this.getProperties(), onError);
    } catch (err) {
      onError(bufferCopy);
      console.error('Failed to flushBuffer', err);
      // XXX (rgoldfinger) temporary to debug circular structures in events
      throw new Error(err);
    }
  }
}

export default new EventTracking(ENABLED === 'true');

const stripHOCNames = (name: string) => {
  const thingsToRemove = ['Apollo', 'FluxContainer', 'Connect', 'WaitForPromises', 'TrackView', /[()-]/g];
  return thingsToRemove.reduce((res, thing) => res.replace(thing, ''), name);
};

export const getEventScreenName = (props: ?Object, router: ?RouterState): ?string => {
  if (props && props.screenName) {
    return props.screenName;
  }
  if (router) {
    const innermostRouteHandlerName = router.routes.reduce((prevName, route) => {
      if (route.component) {
        const { name, displayName } = route.component;
        return displayName || name || prevName;
      } else {
        return prevName;
      }
    }, '');

    if (innermostRouteHandlerName) {
      return stripHOCNames(innermostRouteHandlerName);
    }
  }
  return null;
};

export const getEventElementName = (props: Object) => {
  return props.eventElementName || null;
};

export const getEventContextName = (props: Object, context: Object) => {
  if (props.eventContextName) {
    return props.eventContextName;
  }

  if (context.eventContextName) {
    return context.eventContextName;
  }

  return null;
};

export const getEventProperties = (eventProperties: ?Object, context: Object) => {
  return Object.assign(
    context.eventProperties || {},
    eventProperties || {}
  );
};



// WEBPACK FOOTER //
// ./src/lib/EventTracking.js